import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Pagination } from '../tailgrids/core/pagination.js';
import { TableBody, TableCell, TableHead, TableHeader, TableRoot, TableRow } from '../tailgrids/core/table.js';
import { Skeleton } from './Skeleton.js';
import { cn } from '../../utils/cn.js';

export interface DataTableColumn<T> {
  /** Stable column id (also the sort key). */
  key: string;
  header: React.ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  sortValue?: (row: T) => string | number | null | undefined;
  render: (row: T, index: number) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string | number;
  /** Marks a row that needs attention with a coloured edge. */
  getRowAttention?: (row: T) => 'danger' | 'warning' | undefined;
  /** Makes whole rows open the item (mouse and keyboard). Still link the row's name to its page. */
  onRowClick?: (row: T) => void;
  footer?: React.ReactNode;
  className?: string;
  /** Shows placeholder rows while the first page loads. */
  loading?: boolean;
  /** Rendered instead of the table when there are no rows (an `EmptyState`) — never an empty grid. */
  empty?: React.ReactNode;
  /** Rows per page; omit to show every row. */
  pageSize?: number;
  /** Draws the card frame. Leave off when the table already sits in a card with its toolbar (the list template). */
  framed?: boolean;
  /** Names the table for screen readers when no heading does. */
  'aria-label'?: string;
}

type SortDir = 'asc' | 'desc';

interface SortState {
  column: string | null;
  dir: SortDir;
}

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' };

/** Coloured left edge on the first cell (row borders do not paint in a separated-border table). */
const ATTENTION = {
  danger: '[&>td:first-child]:shadow-[inset_3px_0_0_var(--color-danger)]',
  warning: '[&>td:first-child]:shadow-[inset_3px_0_0_var(--color-warning)]',
};

const LOADING_ROWS = 5;

/**
 * Pure sort helper — exported for unit tests.
 * Nulls/undefined sort last in both asc and desc. Strings use localeCompare; numbers compare numerically.
 */
export function sortRows<T>(rows: T[], column: DataTableColumn<T>, dir: SortDir): T[] {
  if (!column.sortValue) return rows;
  const sv = column.sortValue;
  return [...rows].sort((a, b) => {
    const av = sv(a) ?? null;
    const bv = sv(b) ?? null;
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return dir === 'asc' ? cmp : -cmp;
  });
}

/**
 * The app's data table. Template example: NextAdmin `(dashboard)/(home)/_component/last-transactions-table` and
 * `tables/basic-tables/_component/*` (`TableRoot className="rounded-none border-none"`, gray header band, `px-6` cells).
 * Features: sortable headers (click cycles ascending → descending → off),
 * placeholder rows while loading, an empty state instead of an empty grid, optional pagination.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  getRowAttention,
  onRowClick,
  footer,
  className,
  loading = false,
  empty,
  pageSize,
  framed = false,
  ...aria
}: DataTableProps<T>) {
  const [sort, setSort] = useState<SortState>({ column: null, dir: 'asc' });
  const [page, setPage] = useState(1);

  if (!loading && rows.length === 0 && empty) return <>{empty}</>;

  const toggleSort = (col: DataTableColumn<T>) =>
    setSort((prev) => {
      if (prev.column !== col.key) return { column: col.key, dir: 'asc' };
      if (prev.dir === 'asc') return { column: col.key, dir: 'desc' };
      return { column: null, dir: 'asc' };
    });

  const activeCol = sort.column ? columns.find((c) => c.key === sort.column) : undefined;
  const sorted = activeCol ? sortRows(rows, activeCol, sort.dir) : rows;
  const totalPages = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const currentPage = Math.min(page, totalPages);
  const firstIndex = pageSize ? (currentPage - 1) * pageSize : 0;
  const visible = pageSize ? sorted.slice(firstIndex, firstIndex + pageSize) : sorted;

  return (
    <div
      className={cn('w-full', framed && 'overflow-hidden rounded-xl border border-card-border bg-surface', className)}
    >
      <TableRoot
        className="w-full rounded-none border-none"
        aria-label={aria['aria-label']}
        aria-busy={loading || undefined}
      >
        <TableHeader>
          <TableRow className="[&_th]:border-t bg-background-gray-secondary_alt">
            {columns.map((col) => {
              const sortedHere = sort.column === col.key;
              const ariaSort = col.sortable
                ? sortedHere
                  ? sort.dir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none'
                : undefined;
              const SortIcon = sortedHere ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
              return (
                <TableHead
                  key={col.key}
                  aria-sort={ariaSort}
                  className={cn(
                    'px-6 py-2.5 text-xs leading-4 font-semibold whitespace-nowrap text-text-secondary',
                    ALIGN[col.align ?? 'left'],
                  )}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col)}
                      className="inline-flex cursor-pointer items-center gap-1 rounded-sm hover:text-title-50 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none"
                    >
                      {col.header}
                      <SortIcon size={13} className={sortedHere ? 'text-title-50' : 'text-faint'} aria-hidden="true" />
                    </button>
                  ) : (
                    col.header
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading
            ? Array.from({ length: LOADING_ROWS }, (_, i) => (
                <TableRow key={i}>
                  {columns.map((col) => (
                    <TableCell key={col.key} className="px-6 py-3.5">
                      <Skeleton height={12} width="70%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : visible.map((row, i) => {
                const index = firstIndex + i;
                const attention = getRowAttention?.(row);
                return (
                  <TableRow
                    key={rowKey(row, index)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onKeyDown={
                      onRowClick
                        ? (event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onRowClick(row);
                            }
                          }
                        : undefined
                    }
                    tabIndex={onRowClick ? 0 : undefined}
                    className={cn(
                      'transition-colors',
                      onRowClick &&
                        'cursor-pointer hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none',
                      attention && ATTENTION[attention],
                    )}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={cn(
                          'px-6 py-3.5 align-middle text-sm leading-5 font-normal text-text-primary',
                          ALIGN[col.align ?? 'left'],
                        )}
                      >
                        {col.render(row, index)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
        </TableBody>
        {footer && (
          <tfoot>
            <tr>
              <td colSpan={columns.length} className="border-t border-card-border px-6 py-2.5 text-xs text-muted">
                {footer}
              </td>
            </tr>
          </tfoot>
        )}
      </TableRoot>
      {pageSize && totalPages > 1 && (
        <div className="border-t border-card-border px-6 py-3">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
